import { createContext, useContext, useEffect, useState, useCallback } from "react";

const A11yContext = createContext(null);

const STORAGE_KEY = "aic_a11y";

const loadSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

export const A11yProvider = ({ children }) => {
  const saved = loadSaved();
  const [textSize, setTextSize] = useState(typeof saved.textSize === "number" ? saved.textSize : 0);
  const [highContrast, setHighContrast] = useState(saved.highContrast === true);
  const [pauseMotion, setPauseMotion] = useState(saved.pauseMotion === true);
  const [highlightLinks, setHighlightLinks] = useState(saved.highlightLinks === true);
  const [readFont, setReadFont] = useState(saved.readFont === true);
  const [spacing, setSpacing] = useState(saved.spacing === true);
  const [bigCursor, setBigCursor] = useState(saved.bigCursor === true);

  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle("a11y-text-1", textSize === 1);
    html.classList.toggle("a11y-text-2", textSize === 2);
    html.classList.toggle("a11y-contrast", highContrast);
    html.classList.toggle("a11y-pause", pauseMotion);
    html.classList.toggle("a11y-links", highlightLinks);
    html.classList.toggle("a11y-readfont", readFont);
    html.classList.toggle("a11y-spacing", spacing);
    html.classList.toggle("a11y-cursor", bigCursor);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ textSize, highContrast, pauseMotion, highlightLinks, readFont, spacing, bigCursor }));
  }, [textSize, highContrast, pauseMotion, highlightLinks, readFont, spacing, bigCursor]);

  const cycleTextSize = useCallback(() => setTextSize((s) => (s + 1) % 3), []);

  return (
    <A11yContext.Provider
      value={{
        textSize,
        cycleTextSize,
        highContrast,
        toggleContrast: () => setHighContrast((v) => !v),
        pauseMotion,
        togglePause: () => setPauseMotion((v) => !v),
        highlightLinks,
        toggleLinks: () => setHighlightLinks((v) => !v),
        readFont,
        toggleReadFont: () => setReadFont((v) => !v),
        spacing,
        toggleSpacing: () => setSpacing((v) => !v),
        bigCursor,
        toggleCursor: () => setBigCursor((v) => !v),
        reset: () => {
          setTextSize(0);
          setHighContrast(false);
          setPauseMotion(false);
          setHighlightLinks(false);
          setReadFont(false);
          setSpacing(false);
          setBigCursor(false);
        },
      }}
    >
      {children}
    </A11yContext.Provider>
  );
};

export const useA11y = () => useContext(A11yContext);

export const useMotionOff = () => {
  const ctx = useContext(A11yContext);
  const [systemReduced, setSystemReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setSystemReduced(mq.matches);
    const on = () => setSystemReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return (ctx?.pauseMotion ?? false) || systemReduced;
};
